/**
 * Client-side soft lint for `expression` rule bodies.
 *
 * Mirrors the Python engine's AST whitelist (see
 * `cloud-connector-v3/src/core/alarm_engine.py:compile_expression`) so
 * the SPA can flag obvious syntax mistakes immediately rather than
 * waiting for the edge ACK. **The edge is authoritative** — anything
 * passing this lint may still be rejected upstream, and the SPA must
 * surface the edge's error verbatim.
 *
 * The grammar we accept:
 *
 *     expr     ::= or_expr
 *     or_expr  ::= and_expr (('or' | '||') and_expr)*
 *     and_expr ::= not_expr (('and' | '&&') not_expr)*
 *     not_expr ::= ('not' | '!') not_expr | cmp_expr
 *     cmp_expr ::= add_expr ((>|>=|<|<=|==|!=) add_expr)*
 *     add_expr ::= mul_expr (('+' | '-') mul_expr)*
 *     mul_expr ::= pow_expr (('*'|'/'|'//'|'%') pow_expr)*
 *     pow_expr ::= unary ('**' pow_expr)?
 *     unary    ::= ('+'|'-') unary | atom
 *     atom     ::= NUMBER | 'true' | 'false' | IDENT call_suffix? | '(' expr ')'
 *     call_suffix ::= '(' expr (',' expr)* ')'   // only for whitelisted names
 *
 * Identifiers must be in the declared aliases set. Function calls are
 * limited to `min`, `max`, `abs`, `len` with max depth 2 and ≤ 16 args.
 */
import {
  ALLOWED_EXPRESSION_CALLS,
  MAX_EXPRESSION_CALL_ARGS,
  MAX_EXPRESSION_CALL_DEPTH,
  MAX_EXPRESSION_LENGTH,
  type ExpressionCall,
} from "./engineConstants";

export type LintIssue = {
  severity: "error" | "warning";
  message: string;
  position?: number;
};

export type LintResult = {
  ok: boolean;
  issues: LintIssue[];
  /** Normalised source (`&&`/`||`/`!` replaced with `and`/`or`/`not`). */
  normalised: string;
};

const ALLOWED_CALLS = new Set<ExpressionCall>(ALLOWED_EXPRESSION_CALLS);

const KEYWORDS = new Set([
  "and",
  "or",
  "not",
  "true",
  "false",
  "True",
  "False",
]);

// Token kinds we emit; using strings keeps things easy to debug.
type TokKind =
  | "NUM"
  | "IDENT"
  | "LPAREN"
  | "RPAREN"
  | "COMMA"
  | "OP"
  | "EOF";

type Token = { kind: TokKind; text: string; pos: number };

// -----------------------------------------------------------------------------
// Tokeniser
// -----------------------------------------------------------------------------

const OPERATOR_TOKENS = [
  "**",
  "//",
  "==",
  "!=",
  ">=",
  "<=",
  "&&",
  "||",
  ">",
  "<",
  "+",
  "-",
  "*",
  "/",
  "%",
  "!",
];

function tokenise(src: string): { tokens: Token[]; issues: LintIssue[] } {
  const tokens: Token[] = [];
  const issues: LintIssue[] = [];
  let i = 0;
  const n = src.length;
  while (i < n) {
    const ch = src[i];
    if (ch === " " || ch === "\t" || ch === "\n" || ch === "\r") {
      i++;
      continue;
    }
    if (ch === "(") {
      tokens.push({ kind: "LPAREN", text: "(", pos: i++ });
      continue;
    }
    if (ch === ")") {
      tokens.push({ kind: "RPAREN", text: ")", pos: i++ });
      continue;
    }
    if (ch === ",") {
      tokens.push({ kind: "COMMA", text: ",", pos: i++ });
      continue;
    }
    if ((ch >= "0" && ch <= "9") || (ch === "." && src[i + 1] >= "0" && src[i + 1] <= "9")) {
      let j = i;
      while (
        j < n &&
        ((src[j] >= "0" && src[j] <= "9") || src[j] === "." || src[j] === "e" || src[j] === "E" || src[j] === "+" || src[j] === "-")
      ) {
        // simple float lexer; relies on parseFloat to validate
        j++;
        if (src[j - 1] === "+" || src[j - 1] === "-") {
          // sign only legal right after e/E
          if (src[j - 2] !== "e" && src[j - 2] !== "E") {
            j--;
            break;
          }
        }
      }
      const text = src.slice(i, j);
      if (Number.isNaN(Number(text))) {
        issues.push({
          severity: "error",
          message: `Invalid number ${JSON.stringify(text)}`,
          position: i,
        });
      }
      tokens.push({ kind: "NUM", text, pos: i });
      i = j;
      continue;
    }
    if (ch === "_" || (ch >= "a" && ch <= "z") || (ch >= "A" && ch <= "Z")) {
      let j = i;
      while (j < n) {
        const cj = src[j];
        if (cj === "_" || (cj >= "a" && cj <= "z") || (cj >= "A" && cj <= "Z") || (cj >= "0" && cj <= "9")) {
          j++;
        } else {
          break;
        }
      }
      tokens.push({ kind: "IDENT", text: src.slice(i, j), pos: i });
      i = j;
      continue;
    }
    let matchedOp: string | null = null;
    for (const op of OPERATOR_TOKENS) {
      if (src.startsWith(op, i)) {
        matchedOp = op;
        break;
      }
    }
    if (matchedOp) {
      tokens.push({ kind: "OP", text: matchedOp, pos: i });
      i += matchedOp.length;
      continue;
    }
    issues.push({
      severity: "error",
      message: `Unexpected character ${JSON.stringify(ch)}`,
      position: i,
    });
    i++;
  }
  tokens.push({ kind: "EOF", text: "", pos: src.length });
  return { tokens, issues };
}

// -----------------------------------------------------------------------------
// Parser — recursive descent, walks the AST verifying identifiers + calls
// -----------------------------------------------------------------------------

class Parser {
  private idx = 0;
  private callDepth = 0;
  public issues: LintIssue[] = [];

  constructor(
    private readonly tokens: Token[],
    private readonly allowedAliases: ReadonlySet<string>,
  ) {}

  private peek(off = 0): Token {
    return this.tokens[Math.min(this.tokens.length - 1, this.idx + off)];
  }

  private eat(kind: TokKind, text?: string): Token | null {
    const t = this.peek();
    if (t.kind !== kind) return null;
    if (text !== undefined && t.text !== text) return null;
    this.idx++;
    return t;
  }

  private match(kind: TokKind, text?: string): boolean {
    const t = this.peek();
    if (t.kind !== kind) return false;
    if (text !== undefined && t.text !== text) return false;
    return true;
  }

  private error(message: string, t?: Token) {
    this.issues.push({
      severity: "error",
      message,
      position: t?.pos,
    });
  }

  parseExpression(): void {
    this.parseOr();
    const t = this.peek();
    if (t.kind !== "EOF") {
      this.error(`Unexpected token ${JSON.stringify(t.text)}`, t);
    }
  }

  private parseOr(): void {
    this.parseAnd();
    while (
      (this.match("IDENT", "or") || this.match("OP", "||"))
    ) {
      if (this.match("OP", "||")) {
        this.issues.push({
          severity: "warning",
          message: "`||` accepted; will be normalised to `or` for the edge",
          position: this.peek().pos,
        });
      }
      this.idx++;
      this.parseAnd();
    }
  }

  private parseAnd(): void {
    this.parseNot();
    while ((this.match("IDENT", "and") || this.match("OP", "&&"))) {
      if (this.match("OP", "&&")) {
        this.issues.push({
          severity: "warning",
          message: "`&&` accepted; will be normalised to `and` for the edge",
          position: this.peek().pos,
        });
      }
      this.idx++;
      this.parseNot();
    }
  }

  private parseNot(): void {
    if (this.match("IDENT", "not") || this.match("OP", "!")) {
      if (this.match("OP", "!")) {
        this.issues.push({
          severity: "warning",
          message: "`!` accepted; will be normalised to `not` for the edge",
          position: this.peek().pos,
        });
      }
      this.idx++;
      this.parseNot();
      return;
    }
    this.parseCmp();
  }

  private parseCmp(): void {
    this.parseAdd();
    while (
      this.match("OP", ">") ||
      this.match("OP", ">=") ||
      this.match("OP", "<") ||
      this.match("OP", "<=") ||
      this.match("OP", "==") ||
      this.match("OP", "!=")
    ) {
      this.idx++;
      this.parseAdd();
    }
  }

  private parseAdd(): void {
    this.parseMul();
    while (this.match("OP", "+") || this.match("OP", "-")) {
      this.idx++;
      this.parseMul();
    }
  }

  private parseMul(): void {
    this.parsePow();
    while (
      this.match("OP", "*") ||
      this.match("OP", "/") ||
      this.match("OP", "//") ||
      this.match("OP", "%")
    ) {
      this.idx++;
      this.parsePow();
    }
  }

  private parsePow(): void {
    this.parseUnary();
    if (this.match("OP", "**")) {
      this.idx++;
      this.parsePow();
    }
  }

  private parseUnary(): void {
    if (this.match("OP", "+") || this.match("OP", "-")) {
      this.idx++;
      this.parseUnary();
      return;
    }
    this.parseAtom();
  }

  private parseAtom(): void {
    const t = this.peek();
    if (t.kind === "NUM") {
      this.idx++;
      return;
    }
    if (t.kind === "LPAREN") {
      this.idx++;
      this.parseOr();
      if (!this.eat("RPAREN")) {
        this.error("Expected closing `)`", this.peek());
      }
      return;
    }
    if (t.kind === "IDENT") {
      this.idx++;
      const isLiteralBool = t.text === "true" || t.text === "false" || t.text === "True" || t.text === "False";
      if (this.match("LPAREN")) {
        // Function call.
        if (!ALLOWED_CALLS.has(t.text as ExpressionCall)) {
          this.error(
            `Function ${JSON.stringify(t.text)} is not allowed (allowed: ${[...ALLOWED_CALLS].join(", ")})`,
            t,
          );
        }
        if (this.callDepth + 1 > MAX_EXPRESSION_CALL_DEPTH) {
          this.error(
            `Expression call nesting exceeds depth ${MAX_EXPRESSION_CALL_DEPTH}`,
            t,
          );
        }
        this.callDepth++;
        this.idx++; // consume (
        const args: number = this.parseCallArgs();
        if (args > MAX_EXPRESSION_CALL_ARGS) {
          this.error(
            `Function ${t.text}() has too many arguments (${args} > ${MAX_EXPRESSION_CALL_ARGS})`,
            t,
          );
        }
        if ((t.text === "abs" || t.text === "len") && args !== 1) {
          this.error(`${t.text}() requires exactly 1 argument (got ${args})`, t);
        }
        if ((t.text === "min" || t.text === "max") && args < 1) {
          this.error(`${t.text}() requires at least 1 argument`, t);
        }
        this.callDepth--;
        return;
      }
      if (isLiteralBool || KEYWORDS.has(t.text)) {
        // Bare keyword/literal — no further check.
        return;
      }
      if (!this.allowedAliases.has(t.text)) {
        this.error(
          `Unknown identifier ${JSON.stringify(t.text)} — declare it in the Aliases panel before using it`,
          t,
        );
      }
      return;
    }
    this.error(`Unexpected token ${JSON.stringify(t.text)}`, t);
  }

  private parseCallArgs(): number {
    if (this.match("RPAREN")) {
      this.idx++;
      return 0;
    }
    let n = 0;
    while (true) {
      this.parseOr();
      n++;
      if (this.match("COMMA")) {
        this.idx++;
        continue;
      }
      if (!this.eat("RPAREN")) {
        this.error("Expected `,` or `)` in call arguments", this.peek());
      }
      return n;
    }
  }
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

/** Normalise `&&`/`||`/`!` to `and`/`or`/`not` for the edge parser. */
function normaliseOperators(src: string): string {
  // Order matters: `!=` must stay intact.
  return src
    .replace(/!=/g, " NEQ ")
    .replace(/!/g, " not ")
    .replace(/ NEQ /g, "!=")
    .replace(/&&/g, " and ")
    .replace(/\|\|/g, " or ");
}

export function lintExpression(
  source: string,
  allowedAliases: Iterable<string>,
): LintResult {
  const allowed = new Set<string>(allowedAliases);
  if (typeof source !== "string" || source.trim() === "") {
    return {
      ok: false,
      issues: [{ severity: "error", message: "Expression must not be empty" }],
      normalised: "",
    };
  }
  if (source.length > MAX_EXPRESSION_LENGTH) {
    return {
      ok: false,
      issues: [
        {
          severity: "error",
          message: `Expression too long (${source.length} > ${MAX_EXPRESSION_LENGTH} chars)`,
        },
      ],
      normalised: source,
    };
  }
  const { tokens, issues: lexIssues } = tokenise(source);
  const parser = new Parser(tokens, allowed);
  parser.parseExpression();
  const issues = [...lexIssues, ...parser.issues];
  const hasError = issues.some((i) => i.severity === "error");
  return {
    ok: !hasError,
    issues,
    normalised: normaliseOperators(source),
  };
}
